(() => {
  const MOBILE_MQL = window.matchMedia('(max-width: 749px)');
  const GRID_SCROLL_GAP = 10;

  function getCollectionScrollOffset(target) {
    let offset = GRID_SCROLL_GAP;

    const categories = document.getElementById('collection-categories');
    if (categories) {
      offset += categories.offsetHeight;
    }

    if (MOBILE_MQL.matches && target && !target.matches('[data-filters-aside]')) {
      const filtersAside = document.querySelector('[data-filters-aside]');
      if (filtersAside) {
        const mobileBar = filtersAside.querySelector('.js-collection-filters__mobile-bar');
        if (mobileBar) offset += mobileBar.offsetHeight;

        const activeFilters = filtersAside.querySelector('[data-active-filters]');
        if (activeFilters && !activeFilters.hidden) {
          offset += activeFilters.offsetHeight;
        }
      }
    }

    return offset;
  }

  function getScrollTarget() {
    if (MOBILE_MQL.matches) {
      return (
        document.querySelector('[data-filters-aside]') ||
        document.querySelector('[data-collection-content]')
      );
    }

    return document.querySelector('[data-collection-content]');
  }

  function computeScrollTop(target) {
    const offset = getCollectionScrollOffset(target);
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
    return Math.min(Math.max(0, targetTop), maxScroll);
  }

  function syncScrollMarginTop() {
    const target = getScrollTarget();
    const offset = `${getCollectionScrollOffset(target)}px`;
    const grid = document.getElementById('collection-product-grid');
    const list = document.querySelector('.collection-grid__list');
    const content = document.querySelector('[data-collection-content]');

    if (grid) grid.style.scrollMarginTop = offset;
    if (list) list.style.scrollMarginTop = offset;
    if (content) content.style.scrollMarginTop = offset;
  }

  function scrollToProductGrid(behavior = 'smooth') {
    const target = getScrollTarget();
    if (!target) return;

    syncScrollMarginTop();

    const run = () => {
      const nextTop = computeScrollTop(target);
      if (Math.abs(window.scrollY - nextTop) > 2) {
        window.scrollTo({ top: nextTop, behavior });
      }
    };

    run();
    requestAnimationFrame(run);
    requestAnimationFrame(() => requestAnimationFrame(run));
    window.setTimeout(run, 150);
  }

  function handleInitialHashScroll() {
    if (window.location.hash !== '#collection-product-grid') return;

    const scroll = () => scrollToProductGrid('auto');
    scroll();
    requestAnimationFrame(scroll);
    window.addEventListener('load', scroll, { once: true });
  }

  document.addEventListener('collection:scroll-to-grid', () => scrollToProductGrid('smooth'));
  window.addEventListener('resize', syncScrollMarginTop);
  document.addEventListener('grid:updated', syncScrollMarginTop);

  syncScrollMarginTop();
  handleInitialHashScroll();

  const bannerBtn = document.querySelector('.js-banner-btn');
  if (bannerBtn) {
    bannerBtn.addEventListener('click', (e) => {
      e.preventDefault();
      scrollToProductGrid('smooth');
    });
  }
})();

(() => {
  if (customElements.get('infinite-scroll-grid')) return;

  const DEFAULT_BATCH_SIZE = 12;
  const DEFAULT_BUNDLE_BATCH_SIZE = 10;

  class InfiniteScrollGrid extends HTMLElement {
    constructor() {
      super();
      this.onIntersect = this.onIntersect.bind(this);
      this.loading = false;
      this.pendingItems = [];
    }

    connectedCallback() {
      this.productList = this.querySelector('[data-product-list]');
      this.sentinel = this.querySelector('[data-sentinel]');
      this.sectionId = this.dataset.sectionId;
      this.batchSize = parseInt(this.dataset.batchSize, 10) || DEFAULT_BATCH_SIZE;
      this.bundleBatchSize = parseInt(this.dataset.bundleBatchSize, 10) || DEFAULT_BUNDLE_BATCH_SIZE;
      if (!this.productList) return;

      this.normalizeInitialBatch();
      if (!this.sentinel) {
        if (!this.productList.dataset.nextUrl && this.pendingItems.length === 0) return;
        this.sentinel = this.createSentinel();
      }

      this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '300px' });
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async onIntersect(entries) {
      if (!entries[0].isIntersecting || this.loading) return;

      this.loading = true;
      this.sentinel.setAttribute('data-loading', '');

      try {
        await this.ensurePendingItems();
        this.appendNextBatch();
      } catch (_) {
        this.stopLoading();
      } finally {
        this.loading = false;
        this.sentinel?.removeAttribute('data-loading');
      }
    }

    async ensurePendingItems() {
      const nextUrl = this.productList.dataset.nextUrl;
      if (!nextUrl || this.pendingItems.length >= this.batchSize) return;

      const doc = await this.fetchPage(nextUrl);
      const newList = doc.querySelector('[data-product-list]');
      if (!newList) throw new Error('Product list not found');

      this.pendingItems.push(...newList.querySelectorAll('.js-collection-item'));

      const nextNextUrl = newList.dataset.nextUrl;
      if (nextNextUrl) {
        this.productList.dataset.nextUrl = nextNextUrl;
      } else {
        delete this.productList.dataset.nextUrl;
      }
    }

    normalizeInitialBatch() {
      const initialItems = [...this.productList.querySelectorAll(':scope > .js-collection-item')];
      const initialBatchSize = this.getBatchSizeForItems(initialItems.slice(0, this.batchSize));
      if (initialItems.length <= initialBatchSize) return;

      const overflowItems = initialItems.slice(initialBatchSize);
      overflowItems.forEach((item) => {
        item.remove();
        this.pendingItems.push(item);
      });
    }

    async fetchPage(nextUrl) {
      const url = new URL(nextUrl, window.location.origin);

      if (this.sectionId) {
        url.searchParams.set('sections', this.sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(response.statusText);
        const data = await response.json();
        const html = data[this.sectionId];
        if (!html) throw new Error('Section not found');
        return new DOMParser().parseFromString(html, 'text/html');
      }

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(response.statusText);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    appendNextBatch() {
      if (this.pendingItems.length === 0) {
        if (!this.productList.dataset.nextUrl) this.stopLoading();
        return;
      }

      const batchSize = this.getNextBatchSize();
      const items = this.pendingItems.splice(0, batchSize);
      items.forEach((item) => this.productList.appendChild(item));

      if (this.pendingItems.length === 0 && !this.productList.dataset.nextUrl) {
        this.stopLoading();
      }

      document.dispatchEvent(new CustomEvent('grid:updated'));
    }

    getNextBatchSize() {
      if (!this.productList.dataset.nextUrl && this.pendingItems.length < this.batchSize) {
        return this.pendingItems.length;
      }

      return this.getBatchSizeForItems(this.pendingItems.slice(0, this.batchSize));
    }

    getBatchSizeForItems(items) {
      const hasBundle = items.some((item) => {
        return item.classList.contains('collection-grid__item--bundle') || item.dataset.tags?.includes('do-bundle');
      });

      return hasBundle ? this.bundleBatchSize : this.batchSize;
    }

    createSentinel() {
      const sentinel = document.createElement('div');
      sentinel.className = 'collection-grid__sentinel';
      sentinel.dataset.sentinel = '';
      sentinel.setAttribute('aria-hidden', 'true');
      this.appendChild(sentinel);
      return sentinel;
    }

    stopLoading() {
      if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
      this.sentinel?.remove();
    }
  }

  customElements.define('infinite-scroll-grid', InfiniteScrollGrid);
})();
